<template>
	<CollapsibleSection class="item_detail"
		title="Detalle del mes"
		:default-expanded="false"
		@change="onToggle">
		<!-- El modo sólo se ofrece con la sección abierta: cambiar la vista de
			algo que no se ve no le sirve a nadie. -->
		<template v-if="isExpanded" #title-actions>
			<div class="mode_switch" role="group" aria-label="Abrir los costos por">
				<button v-for="option in modeOptions" :key="option.value"
					type="button"
					class="mode_btn"
					:class="{ 'is-active': mode === option.value }"
					:aria-pressed="String(mode === option.value)"
					@click="$emit('update:mode', option.value)">
					{{ option.label }}
				</button>
			</div>
		</template>

		<!-- El "Cargando" a pantalla completa es SÓLO el primer despliegue.
			Al cambiar de ítem se conserva lo que ya está en pantalla y se
			atenúa lo que va a cambiar: la lista de ítems no cambia con la
			selección, así que sigue viva y se puede elegir otra sin esperar. -->
		<p v-if="isLoading && !data" class="detail_state">Cargando detalle…</p>
		<p v-else-if="error" class="detail_state detail_state--error">{{ error }}</p>
		<div v-else-if="data" class="detail_grid">
			<CategoryItemsCard v-if="mode === 'items'" class="cell"
				:items="items"
				:category-plural="categoryPlural"
				:selected-item="selectedItem"
				:total="itemsTotal"
				@select-item="$emit('select-item', $event)" />

			<AccountStatementCard v-else class="cell"
				:class="{ 'is-busy': isLoading }"
				title="Costos"
				:period-label="data.period_label"
				:headline="data.headline"
				:statement="data.statement"
				:selected-account="selectedAccount"
				:selected-level="selectedLevel"
				@select-account="$emit('select-account', $event)" />

			<CounterpartsRankingCard class="cell"
				:class="{ 'is-busy': isLoading }"
				:value="query"
				:counterparts="counterparts"
				@input="$emit('update:query', $event)"
				@counterpart-click="$emit('counterpart-click', $event)" />
		</div>
	</CollapsibleSection>
</template>

<script>
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import AccountStatementCard from "@/components/financialReports/AccountStatementCard.vue";
import CategoryItemsCard from "./CategoryItemsCard.vue";
import CounterpartsRankingCard
	from "@/components/financialReports/costsAnalysis/CounterpartsRankingCard.vue";

// Sección "Cuentas & Contrapartes" del drawer de categorías: la cascada de
// costos del mes (con sus cuentas y márgenes) y el ranking de contrapartes.
// Las dos cards son las mismas del drawer de Costos — el detalle lo sirve
// `/costs/analysis` del mismo mes, así que no hay backend propio.
//
// Sin selector de ítem (Steve, 2026-09-16: la leyenda y los chips no se
// entendían): la sección muestra el mes completo de la categoría, acotado por
// lo que el analista haya filtrado en el header del drawer. Presentacional:
// el padre (BodyProfitabilityAnalysis) la conecta al store.
//
// Clickear una fila de la cascada recorta el ranking a esa cuenta, igual que
// en el drawer de Costos: la card emite `select-account` y la sección lo pasa
// al padre, que despacha el recorte.
//
// La card izquierda tiene dos vistas del mes (segmented control del header):
// "Por tienda" —los ítems de la categoría con su venta, participación y
// margen, que acotan TODO el detalle al ítem elegido— y "Por cuenta" —la
// cascada contable de costos, que recorta el ranking a una cuenta—. Los dos
// filtros se combinan: el recorte por cuenta viaja con el ítem puesto.
export default {
	name: "ItemDetailSection",

	components: {
		CollapsibleSection, AccountStatementCard, CategoryItemsCard, CounterpartsRankingCard,
	},

	props: {
		// Payload de /costs/analysis del mes.
		data: { type: Object, default: null },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		// Buscador del ranking (v-model del padre).
		query: { type: String, default: "" },
		counterparts: { type: Array, default: () => [] },
		// Cuenta que recorta el ranking (la gobierna el store). Null = todas.
		selectedAccount: { type: String, default: null },
		// Nivel de `selectedAccount`: "2" (grupo) o "3" (cuenta). Null con todas.
		selectedLevel: { type: String, default: null },
		// Costos por ítem de la categoría: [{ name, costs, share, color }].
		items: { type: Array, default: () => [] },
		// Categoría en plural ("tiendas") y en singular ("tienda").
		categoryPlural: { type: String, default: "ítems" },
		categorySingular: { type: String, default: "ítem" },
		// Ítem elegido. Null = todos (con el filtro del header).
		selectedItem: { type: String, default: null },
		// Vista de la card izquierda: "items" | "accounts". La gobierna el
		// store (decide qué datos hay que pedir), la card sólo la emite.
		mode: { type: String, default: "items" },
		// Costo del mes de la categoría completa.
		itemsTotal: { type: Number, default: 0 },
	},

	data() {
		return { isExpanded: false };
	},

	computed: {
		modeOptions() {
			return [
				{ value: "items", label: `Por ${this.categorySingular}` },
				{ value: "accounts", label: "Por cuenta" },
			];
		},
	},

	methods: {
		// Fetch lazy: el detalle se pide al desplegar, no al abrir el drawer.
		onToggle(expanded) {
			this.isExpanded = expanded;
			if (expanded) this.$emit("expand");
		},
	},
};
</script>

<style scoped lang="scss">
.item_detail {
	margin-top: 16px;
}

// Alto FIJO para las dos cards: cada una scrollea adentro (las dos están
// hechas para que el padre las clampee). Sin esto, el ranking de contrapartes
// crecía sin techo y el drawer terminaba con un scroll kilométrico.
.detail_grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	align-items: stretch;
	height: min(560px, 60vh);
	min-height: 320px;
}

.cell {
	min-height: 0;
}

// Card esperando datos del ítem recién elegido: se atenúa y no acepta clicks,
// pero se sigue leyendo — cambiar de ítem no debe vaciar la pantalla.
.cell.is-busy {
	opacity: 0.5;
	pointer-events: none;
	transition: opacity 120ms ease;
}

// Segmented control del header: dos vistas del mismo costo del mes.
.mode_switch {
	display: inline-flex;
	padding: 2px;
	gap: 2px;
	background: var(--report-surface);
	border: 1px solid var(--report-border);
	border-radius: 999px;
}

.mode_btn {
	border: 0;
	border-radius: 999px;
	padding: 4px 12px;
	background: none;
	font-family: inherit;
	font-size: 12px;
	font-weight: 600;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	white-space: nowrap;

	&:hover {
		color: var(--report-text);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 1px;
	}

	&.is-active {
		background: var(--report-bg);
		color: var(--report-accent-text);
		box-shadow: var(--report-shadow-card);
	}
}

.detail_state {
	padding: 24px 0;
	text-align: center;
	color: var(--report-text-muted);
	font-size: 13px;

	&--error {
		color: var(--report-negative);
	}
}

// En una columna cada card conserva su propio alto y su propio scroll.
@media (max-width: 1100px) {
	.detail_grid {
		grid-template-columns: 1fr;
		grid-auto-rows: min(420px, 55vh);
		height: auto;
	}
}
</style>
